import { KeyRoundIcon } from '@/components/atoms/icons/KeyRoundIcon';
import { FullScreenLoader } from '@/components/molecules/FullScreenLoader';
import { OkDialog } from '@/components/molecules/dialogs/OkDialog';
import { OTPVerificationScreen } from '@/components/organisms/OTPVerificationScreen';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { logger } from '@/core/logger';
import { useForgotPassword } from '@/modules/onboarding/account-recovery/forgot-password/hooks';
import { useRouter } from 'expo-router';
import React from 'react';

export default function ForgotPasswordRoute() {
    useCatalogDynatraceView(DT_SCREEN.FORGOT_PASSWORD, 'forgot_password_viewed', true);
    const { sendResetLink, verifyOTP, transactionId, oid, email, otp, success, loading, verifyingOTP, error, clearError } = useForgotPassword();
    const { t } = useTranslation();
    const router = useRouter();
    const [otpSuccess, setOtpSuccess] = React.useState(false);
    const [dialogVisible, setDialogVisible] = React.useState(false);
    const [dialogMessage, setDialogMessage] = React.useState('');

    React.useEffect(() => {
        if (error) {
            setDialogMessage(error);
            setDialogVisible(true);
        }
    }, [error]);

    const handleEmailSubmit = async (emailInput: string) => {
        await sendResetLink(emailInput);
        // transactionId is automatically stored in hook state and will be available after state update
        setOtpSuccess(false); // Reset success state when requesting new OTP
    };

    const handleOTPComplete = async (otpInput: string) => {
        logger.info('ForgotPassword', 'OTP entered, verifying...', { otp: otpInput, transactionId });
        setOtpSuccess(false); // Reset success state before verification
        const result = await verifyOTP(otpInput);

        if (result.success) {
            trackCatalogEvent({
                screen: DT_SCREEN.FORGOT_PASSWORD,
                actionName: 'forgot_password_otp_verify_success',
                eventType: 'action',
            });
            // Use OID directly from the response result
            const responseOid = result.oid;
            const currentEmail = email;
            
            logger.info('ForgotPassword', 'OTP verified successfully, navigating to set new password', { 
                transactionId,
                oid: responseOid,
                email: currentEmail,
                otp: otpInput,
            });
            
            if (!responseOid) {
                logger.error('ForgotPassword', 'OID is missing after verification, cannot proceed');
                setOtpSuccess(false);
                return;
            }
            
            setOtpSuccess(true);
            // Navigate to set new password screen after successful OTP verification
            // Pass all required parameters: oid, otp, email, transactionId
            const params = new URLSearchParams({
                forgot: 'true',
                transactionId: transactionId || '',
                oid: responseOid,
                otp: otpInput,
                email: currentEmail || '',
            });
            router.replace(`/set-new-password?${params.toString()}` as any);
        } else {
            trackCatalogEvent({
                screen: DT_SCREEN.FORGOT_PASSWORD,
                actionName: 'forgot_password_otp_verify_error',
                eventType: 'error',
                properties: { p_failedMessage: error ?? 'OTP verification failed' },
            });
            logger.warn('ForgotPassword', 'OTP verification failed', { error, transactionId });
            setOtpSuccess(false);
        }
    };

    const handleResendOTP = () => {
        // Resend OTP by calling sendResetLink again to get new transactionId
        // Note: This would need the email address, which we might need to store
        logger.info('ForgotPassword', 'Resend OTP requested', { transactionId });
        // TODO: Implement resend logic if email is stored
    };

    const handleOTPChange = (value: string) => {
        // Clear error and success state when user starts typing again
        if (error && value.length === 0) {
            clearError();
        }
        if (otpSuccess && value.length < 4) {
            setOtpSuccess(false);
        }
    };

    return (
        <>
            <OTPVerificationScreen
                type="email"
                mode="inline"
                headerType="logo"
                customIcon={<KeyRoundIcon size={40} />}
                customTitle={t('auth.forgotPassword')}
                customDescription={t('auth.forgotPasswordDescription')}
                submitButtonText={t('auth.sendResetLink')}
                onContactSubmit={handleEmailSubmit}
                onOTPComplete={handleOTPComplete}
                onResendOTP={handleResendOTP}
                onOTPChange={handleOTPChange}
                showOTP={success}
                loading={loading}
                verifyingOTP={verifyingOTP}
                otpError={error}
                otpSuccess={otpSuccess}
            />
            <FullScreenLoader visible={loading || verifyingOTP} />
            <OkDialog
                visible={dialogVisible}
                message={dialogMessage}
                onOk={() => {
                    setDialogVisible(false);
                    clearError();
                }}
            />
        </>
    );
}